import { z } from 'zod';

// Resources CSV export (T-107). A shared catalog of exportable fields drives
// both the FE field-selector modal and the BE column allowlist, so the two
// never drift. Sensitive vendor fields (EIN, ACH) are deliberately absent —
// they are never exportable. Rate fields are `gated` (admin-only today;
// owner-only once that role lands, T-117).

export const ExportGrain = z.enum(['resource', 'engagement']);
export type ExportGrain = z.infer<typeof ExportGrain>;

export type ExportFieldGroup = 'resource' | 'vendor' | 'engagement';

export interface ExportField {
  key: string;
  group: ExportFieldGroup;
  /** Always included; cannot be unchecked (a row needs an identifier). */
  required?: boolean;
  /** Margin-revealing rate field — admin-only (owner-only in Wave 3). */
  gated?: boolean;
  /** Checked by default in the modal + included when no explicit field list is given. */
  defaultOn?: boolean;
}

export const EXPORT_FIELDS: readonly ExportField[] = [
  { key: 'resource_name', group: 'resource', required: true, defaultOn: true },
  { key: 'role', group: 'resource', defaultOn: true },
  { key: 'worker_id', group: 'resource', defaultOn: true },
  { key: 'resource_email', group: 'resource', defaultOn: true },
  { key: 'mobile_phone', group: 'resource', defaultOn: true },
  { key: 'mailing_street', group: 'resource', defaultOn: true },
  { key: 'mailing_city', group: 'resource', defaultOn: true },
  { key: 'mailing_state', group: 'resource', defaultOn: true },
  { key: 'mailing_zip', group: 'resource', defaultOn: true },
  { key: 'created_at', group: 'resource', defaultOn: false },
  { key: 'vendor_name', group: 'vendor', defaultOn: true },
  { key: 'company_type', group: 'vendor', defaultOn: true },
  { key: 'vendor_city', group: 'vendor', defaultOn: true },
  { key: 'vendor_state', group: 'vendor', defaultOn: true },
  { key: 'client', group: 'engagement', defaultOn: true },
  { key: 'status', group: 'engagement', defaultOn: true },
  { key: 'start_date', group: 'engagement', defaultOn: false },
  { key: 'end_date', group: 'engagement', defaultOn: false },
  { key: 'rate_to_vendor', group: 'engagement', gated: true, defaultOn: false },
  { key: 'rate_to_client', group: 'engagement', gated: true, defaultOn: false },
];

export const EXPORT_FIELD_KEYS: readonly string[] = EXPORT_FIELDS.map((f) => f.key);

const VALID = new Set(EXPORT_FIELD_KEYS);
const GATED = new Set(EXPORT_FIELDS.filter((f) => f.gated).map((f) => f.key));
const REQUIRED = EXPORT_FIELDS.filter((f) => f.required).map((f) => f.key);

/**
 * Resolve the effective column set for an export. Drops unknown keys, drops
 * gated (rate) keys when the viewer is not an admin, force-includes required
 * keys, dedupes, and returns them in catalog order. Used by the FE for the live
 * preview and by the BE as the authoritative column allowlist — same answer on
 * both sides. With no requested list, returns the default-on, role-allowed set.
 */
export function resolveExportFields(
  requested: readonly string[] | undefined,
  isAdmin: boolean,
): string[] {
  const allowedByRole = (k: string): boolean => isAdmin || !GATED.has(k);
  const base =
    requested && requested.length > 0
      ? requested.filter((k) => VALID.has(k) && allowedByRole(k))
      : EXPORT_FIELDS.filter((f) => f.defaultOn && allowedByRole(f.key)).map((f) => f.key);
  const chosen = new Set(base);
  for (const k of REQUIRED) chosen.add(k);
  return EXPORT_FIELD_KEYS.filter((k) => chosen.has(k));
}

// Query schema for GET /api/resources/export.csv. `fields` is a comma-separated
// list; absent => the default-on set for the viewer's role.
export const ResourceExportQuery = z.object({
  grain: ExportGrain.default('resource'),
  fields: z.string().optional(),
});
export type ResourceExportQuery = z.infer<typeof ResourceExportQuery>;
